import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { ApiError } from '../../lib/api';
import { operatorConsole, type MediaKitPackResponse } from '../../lib/api/operatorConsole';
import { CardTile } from './CardTile';
import { LoadError } from './LoadError';
import { PackHeader } from './PackHeader';
import { errorText } from './mediaKitErrors';

export function MediaKitPackView({ packId }: { packId: number }) {
  const [data, setData] = useState<MediaKitPackResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    operatorConsole.mediaKit
      .getPack(packId)
      .then((loaded) => {
        if (alive) setData(loaded);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setMissing(true);
          return;
        }
        setError(errorText(err));
      });
    return () => {
      alive = false;
    };
  }, [packId, attempt]);

  function retry() {
    setError(null);
    setAttempt((n) => n + 1);
  }

  // 404 is either a pack not enabled for us or the whole feature switched off;
  // the list tells the two apart (it sends a hidden feature on to the dashboard).
  if (missing) return <Navigate to="/media-kit" replace />;

  return (
    <div className="space-y-5 p-6">
      <Link to="/media-kit" className="text-[12px] text-[var(--co-muted)] hover:underline">
        ← Media Kit
      </Link>

      {error ? (
        <LoadError message={error} onRetry={retry} />
      ) : data === null ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading pack…</p>
      ) : (
        <>
          <PackHeader pack={data.pack} cardsCount={data.cards.length} />

          <section aria-labelledby="media-kit-top-cards" className="space-y-3">
            <h2 id="media-kit-top-cards" className="text-[15px] font-semibold text-[var(--co-text)]">
              Top cards
            </h2>
            {data.cards.length === 0 ? (
              <div className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 text-center text-[13px] text-[var(--co-muted)]">
                No top cards in this pack right now.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {data.cards.map((card) => (
                  <CardTile key={card.id} packId={data.pack.id} card={card} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
